import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Mail, RefreshCw, ArrowLeft, CheckCircle } from "lucide-react";
import { apiClient } from "@/api/ApiClient";
import { useToast } from "@/hooks/use-toast";

export const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();

  const email = searchParams.get('email') || '';

  // Redirect to home if no email parameter
  useEffect(() => {
    if (!email) {
      navigate('/');
    }
  }, [email, navigate]);

  const handleResendEmail = async () => {
    if (!email) return;

    setIsResending(true);
    try {
      await apiClient.post('/auth/resend-verification', { email });
      
      setEmailSent(true);
      toast({
        title: "Email sent! 💌",
        description: "Check your inbox for the verification email.",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to resend email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  if (!email) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-dreamy flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-card/80 backdrop-blur-sm border-primary/20">
        <div className="text-center space-y-6">
          {/* Icon and Title */}
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
              Check Your Email
            </h1>
          </div>

          {/* Email sent message - in the center */}
          <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-primary/10">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <p className="text-sm font-medium text-foreground">
                Verification email sent!
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              We've sent a verification link to{" "}
              <span className="font-medium text-foreground">{email}</span>
            </p>
          </div>

          {/* Instructions */}
          <div className="text-left space-y-3">
            <h3 className="font-medium text-sm text-foreground">What's next?</h3>
            <ol className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="bg-primary/20 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium mt-0.5">
                  1
                </span>
                <span>Check your email inbox (and spam folder)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-primary/20 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium mt-0.5">
                  2
                </span>
                <span>Click the verification link in the email</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-primary/20 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium mt-0.5">
                  3
                </span>
                <span>You'll be redirected back to start chatting!</span>
              </li>
            </ol>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={handleResendEmail}
              variant="outline"
              className="w-full border-primary/20 hover:bg-primary/5"
              disabled={isResending}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isResending ? 'animate-spin' : ''}`} />
              {isResending ? 'Sending...' : emailSent ? 'Resend Email' : 'Resend Verification Email'}
            </Button>

            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>

          {/* Footer note */}
          <div className="text-xs text-muted-foreground pt-4 border-t border-primary/10">
            <p>
              Having trouble? The verification link will expire in 24 hours.
              <br />
              You can resend the email if needed.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};